/* ============================================================
   MORNING FIX COFFEE
   BUILD #4 — CART OPERATIONS
   VERSION 1.0.1
   ============================================================

   RESPONSIBILITY
   ------------------------------------------------------------
   Execute Shopify cart operations using resolved product,
   variant, and subscription selling-plan information supplied
   by Builds #3 and #5.

   Build #4 DOES NOT:
   • calculate quiz results
   • determine roast profiles
   • select landing pages
   • define products
   • define variant IDs
   • define subscription plans
   • render landing-page UI

   Build #1 = Profile Engine
   Build #2 = Landing Page Router
   Build #3 = Shopify Product Configuration
   Build #4 = Cart Operations
   Build #5 = Subscription Experience

   ============================================================ */


/* ============================================================
   BUILD #4
   ============================================================ */

const CoffeeCart = (function () {


  /* ==========================================================
     1. SHOPIFY CART ENDPOINT
     ========================================================== */

  const CART_ADD_URL =
    "/cart/add.js";


  /* ==========================================================
     2. POST ITEMS TO SHOPIFY CART
     ==========================================================

     Centralized Shopify cart POST operation.

     All /cart/add.js requests pass through this function.

     ========================================================== */

  async function postCartItems(items) {

    if (
      !Array.isArray(items) ||
      items.length === 0
    ) {

      throw new Error(
        "At least one cart item is required."
      );

    }


    const response =
      await fetch(
        CART_ADD_URL,
        {

          method: "POST",

          headers: {

            "Content-Type":
              "application/json",

            "Accept":
              "application/json"

          },

          body:
            JSON.stringify({

              items:
                items

            })

        }
      );


    /* ========================================================
       SHOPIFY ERROR HANDLING
       ======================================================== */

    if (!response.ok) {

      let message =
        "Shopify cart request failed.";


      try {

        const error =
          await response.json();


        if (
          error &&
          error.description
        ) {

          message =
            error.description;

        }

      } catch (error) {

        /* Keep default error message. */

      }


      throw new Error(
        message
      );

    }


    return response.json();

  }


  /* ==========================================================
     3. VALIDATE RESOLVED PRODUCT
     ==========================================================

     Used by normal single-product and Buy Both purchases.

     Build #3 is authoritative for:
     • key
     • name
     • variantId
     • available

     ========================================================== */

  function validateResolvedProduct(
    product
  ) {

    if (!product) {

      throw new Error(
        "Resolved Shopify product is required."
      );

    }


    if (!product.key) {

      throw new Error(
        "Resolved Shopify product has no product key."
      );

    }


    if (product.available === false) {

      throw new Error(
        "Product is currently unavailable: " +
        product.name
      );

    }


    if (!product.variantId) {

      throw new Error(
        "No Shopify variant ID configured for: " +
        product.key
      );

    }


    return true;

  }


  /* ==========================================================
     4. ADD SINGLE RESOLVED PRODUCT
     ========================================================== */

  async function addSingle(
    product,
    quantity
  ) {

    validateResolvedProduct(
      product
    );


    const qty =
      quantity || 1;


    if (qty < 1) {

      throw new Error(
        "Cart quantity must be at least 1."
      );

    }


    return postCartItems(
      [

        {

          id:
            product.variantId,

          quantity:
            qty

        }

      ]
    );

  }


  /* ==========================================================
     5. ADD BOTH RESOLVED PRODUCTS
     ==========================================================

     Used by transition profiles:

     R2 = Medium-Light + Medium
     R4 = Medium + Medium-Dark
     R6 = Medium-Dark + Dark

     Both products are submitted in ONE Shopify request.

     ========================================================== */

  async function addBoth(
    products,
    quantity
  ) {

    if (
      !Array.isArray(products)
    ) {

      throw new Error(
        "Buy Both requires an array of resolved products."
      );

    }


    if (
      products.length !== 2
    ) {

      throw new Error(
        "Buy Both requires exactly two products."
      );

    }


    const qty =
      quantity || 1;


    if (qty < 1) {

      throw new Error(
        "Cart quantity must be at least 1."
      );

    }


    /* ========================================================
       VALIDATE BOTH PRODUCTS
       ======================================================== */

    products.forEach(
      function(product) {

        validateResolvedProduct(
          product
        );

      }
    );


    /* ========================================================
       BUILD ONE MULTI-ITEM REQUEST
       ======================================================== */

    const items =
      products.map(
        function(product) {

          return {

            id:
              product.variantId,

            quantity:
              qty

          };

        }
      );


    return postCartItems(
      items
    );

  }


  /* ==========================================================
     6. VALIDATE SUBSCRIPTION OPTION
     ==========================================================

     Build #5 intentionally uses a different object shape from
     Build #3.

     Build #5 fields:
     • productKey
     • productName
     • variantId
     • planKey
     • sellingPlanId
     • available

     Therefore this has its own validator rather than reusing
     validateResolvedProduct().

     ========================================================== */

  function validateSubscriptionOption(
    option
  ) {

    if (!option) {

      throw new Error(
        "Subscription option is required."
      );

    }


    if (
      option.type !==
      "subscription"
    ) {

      throw new Error(
        "Invalid subscription option type."
      );

    }


    if (!option.productKey) {

      throw new Error(
        "Subscription option has no product key."
      );

    }


    if (!option.productName) {

      throw new Error(
        "Subscription option has no product name."
      );

    }


    if (!option.variantId) {

      throw new Error(
        "No Shopify variant ID configured for subscription product: " +
        option.productKey
      );

    }


    if (!option.planKey) {

      throw new Error(
        "Subscription option has no plan key."
      );

    }


    if (!option.sellingPlanId) {

      throw new Error(
        "No Shopify selling plan ID configured for: " +
        option.planKey
      );

    }


    if (
      option.available === false
    ) {

      throw new Error(
        "Subscription is currently unavailable: " +
        option.productName
      );

    }


    return true;

  }


  /* ==========================================================
     7. ADD SUBSCRIPTION
     ==========================================================

     Shopify requires:

     {
       id,
       quantity,
       selling_plan
     }

     The selling_plan value comes directly from Build #5.

     ========================================================== */

  async function addSubscription(
    option,
    quantity
  ) {

    validateSubscriptionOption(
      option
    );


    const qty =
      quantity || 1;


    if (qty < 1) {

      throw new Error(
        "Cart quantity must be at least 1."
      );

    }


    return postCartItems(
      [

        {

          id:
            option.variantId,

          quantity:
            qty,

          selling_plan:
            option.sellingPlanId

        }

      ]
    );

  }


  /* ==========================================================
     7B. ADD BOTH SUBSCRIPTION
     ==========================================================

     Used when the customer selects "Buy Both" as their
     subscription coffee choice. Unlike a normal Buy Both
     purchase, Shopify has no concept of a single line item
     representing two subscribed products — each product needs
     its own line item, but BOTH carry the same selling_plan.

     Build #5 does not build this directly (buildPurchaseOption
     only ever handles one product at a time); the caller is
     expected to call buildPurchaseOption() twice, once per
     product, using the same planKey both times, and pass the
     resulting two subscription options here.

     ========================================================== */

  async function addBothSubscription(
    options,
    quantity
  ) {

    if (
      !Array.isArray(options)
    ) {

      throw new Error(
        "Buy Both subscription requires an array of subscription options."
      );

    }


    if (
      options.length !== 2
    ) {

      throw new Error(
        "Buy Both subscription requires exactly two subscription options."
      );

    }


    const qty =
      quantity || 1;


    if (qty < 1) {

      throw new Error(
        "Cart quantity must be at least 1."
      );

    }


    /* ========================================================
       VALIDATE BOTH SUBSCRIPTION OPTIONS
       ======================================================== */

    options.forEach(
      function(option) {

        validateSubscriptionOption(
          option
        );

      }
    );


    /* ========================================================
       BUILD ONE MULTI-ITEM REQUEST, EACH CARRYING selling_plan
       ======================================================== */

    const items =
      options.map(
        function(option) {

          return {

            id:
              option.variantId,

            quantity:
              qty,

            selling_plan:
              option.sellingPlanId

          };

        }
      );


    return postCartItems(
      items
    );

  }


  /* ==========================================================
     8. ADD PURCHASE OPTION
     ==========================================================

     Supported purchase types:

     • single
     • both
     • subscription

     ========================================================== */

  async function addPurchaseOption(
    option,
    quantity
  ) {

    if (!option) {

      throw new Error(
        "Purchase option is required."
      );

    }


    /* ========================================================
       SINGLE PRODUCT
       ======================================================== */

    if (
      option.type ===
      "single"
    ) {

      return addSingle(
        option,
        quantity
      );

    }


    /* ========================================================
       BOTH PRODUCTS
       ======================================================== */

    if (
      option.type ===
      "both"
    ) {

      if (
        !Array.isArray(
          option.products
        )
      ) {

        throw new Error(
          "Buy Both option contains no products."
        );

      }


      if (
        option.products.length !== 2
      ) {

        throw new Error(
          "Buy Both option must contain exactly two products."
        );

      }


      return addBoth(
        option.products,
        quantity
      );

    }


    /* ========================================================
       SUBSCRIPTION
       ======================================================== */

    if (
      option.type ===
      "subscription"
    ) {

      return addSubscription(
        option,
        quantity
      );

    }


    /* ========================================================
       UNKNOWN OPTION
       ======================================================== */

    throw new Error(
      "Unknown purchase option type: " +
      option.type
    );

  }


  /* ==========================================================
     9. ADD FROM LANDING PAGE
     ==========================================================

     The external calling convention remains unchanged.

     ========================================================== */

  async function addFromLandingPage(
    landingPage,
    optionKey,
    quantity
  ) {

    if (!landingPage) {

      throw new Error(
        "Landing page configuration is required."
      );

    }


    if (
      !Array.isArray(
        landingPage.purchaseOptions
      )
    ) {

      throw new Error(
        "Landing page contains no purchase options."
      );

    }


    const option =
      landingPage.purchaseOptions.find(
        function(purchaseOption) {

          return (
            purchaseOption.key ===
            optionKey
          );

        }
      );


    if (!option) {

      throw new Error(
        "Purchase option not found: " +
        optionKey
      );

    }


    return addPurchaseOption(
      option,
      quantity
    );

  }


  /* ==========================================================
     10. ADD THEN GO TO CART
     ==========================================================

     Navigation occurs ONLY after a successful cart request.

     ========================================================== */

  async function addAndGoToCart(
    landingPage,
    optionKey,
    quantity
  ) {

    await addFromLandingPage(
      landingPage,
      optionKey,
      quantity
    );


    window.location.href =
      "/cart";

  }


  /* ==========================================================
     11. GET CART
     ========================================================== */

  async function getCart() {

    const response =
      await fetch(
        "/cart.js",
        {

          method:
            "GET",

          headers: {

            "Accept":
              "application/json"

          }

        }
      );


    if (!response.ok) {

      throw new Error(
        "Unable to retrieve Shopify cart."
      );

    }


    return response.json();

  }


  /* ==========================================================
     12. CLEAR CART
     ========================================================== */

  async function clearCart() {

    const response =
      await fetch(
        "/cart/clear.js",
        {

          method:
            "POST",

          headers: {

            "Accept":
              "application/json"

          }

        }
      );


    if (!response.ok) {

      throw new Error(
        "Unable to clear Shopify cart."
      );

    }


    return response.json();

  }


  /* ==========================================================
     13. PUBLIC API
     ========================================================== */

  return {

    version:
      "1.0.1",

    build:
      4,

    postCartItems,

    validateResolvedProduct,

    addSingle,

    addBoth,

    validateSubscriptionOption,

    addSubscription,

    addBothSubscription,

    addPurchaseOption,

    addFromLandingPage,

    addAndGoToCart,

    getCart,

    clearCart

  };

})();


/* ============================================================
   14. GLOBAL EXPORT
   ============================================================ */

if (
  typeof window !== "undefined"
) {

  window.CoffeeCart =
    CoffeeCart;

}


/* ============================================================
   END BUILD #4
   ============================================================ */